import React from 'react'
import * as _ from 'lodash'
import { Row, Col, Input, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  PlusCircleTwoTone,
  MinusCircleTwoTone,
  ExclamationCircleOutlined,
  CaretUpFilled,
  CaretDownFilled,
} from '@ant-design/icons'
import BlockElement from '../../../../components/BlockElement'
import { plusIconStyle, minusIconStyle, arrowIconStyle } from '../styles'
import { switchElements, disabledMove } from '../utils'

interface Props {
  structure: any
  setChart: any
}

const ChartComposition = (props: Props): JSX.Element => {
  const { t } = useTranslation()

  const setStructure = (structure: any) => {
    props.setChart((chart: any) => {
      return { ...chart, structure: structure }
    })
  }

  const addElement = (mainElement: string) => {
    const structure = {
      elements: [...props.structure.elements, mainElement],
      hierarchy: { ...props.structure.hierarchy, [mainElement]: [] },
    }
    props.setChart((chart: any) => {
      const values = _.cloneDeep(chart.values)
      Object.keys(chart.values).forEach(society => {
        values[society][mainElement] = {}
      })
      return {
        ...chart,
        structure: structure,
        values: values,
      }
    })
  }

  const removeElement = (mainElement: string) => {
    const { ...hierarchy } = props.structure.hierarchy
    const { ...elements } = props.structure.elements

    delete elements[Object.values(elements).indexOf(mainElement)]
    props.setChart((chart: any) => {
      const values = _.cloneDeep(chart.values)
      Object.keys(chart.values).forEach(society => {
        delete values[society][mainElement]
      })
      return {
        ...chart,
        values: values,
        structure: { elements: Object.values(elements), hierarchy: hierarchy },
      }
    })
  }

  const updateElement = (mainElement: string) => (e: React.ChangeEvent) => {
    const newValue = (e.target as HTMLInputElement).value

    const elements = [...props.structure.elements]
    const index = props.structure.elements.indexOf(mainElement)
    elements[index] = newValue

    const { [mainElement]: remove, ...hierarchy } = props.structure.hierarchy
    hierarchy[newValue] = remove

    props.setChart((chart: any) => {
      const values = _.cloneDeep(chart.values)
      Object.keys(chart.values).forEach(society => {
        values[society][newValue] = values[society][mainElement]
        delete values[society][mainElement]
      })
      return {
        ...chart,
        values: values,
        structure: { elements: elements, hierarchy: hierarchy },
      }
    })
  }

  const addSubElement = (mainElement: string, subElement: string) => {
    const newHierarchy = [...props.structure.hierarchy[mainElement]]
    newHierarchy.push(subElement)

    props.setChart((chart: any) => {
      const values = _.cloneDeep(chart.values)
      Object.keys(chart.values).forEach(society => {
        values[society][mainElement][subElement] = { type: 'none' }
      })
      return {
        ...chart,
        values: values,
        structure: {
          ...props.structure,
          hierarchy: {
            ...props.structure.hierarchy,
            [mainElement]: Object.values(newHierarchy),
          },
        },
      }
    })
  }

  const reorderElements = (element: string, move: 'up' | 'down') => {
    const elements = [...props.structure.elements]
    if (!disabledMove(elements, element, move)) {
      switchElements(elements, element, move)
      setStructure({ ...props.structure, elements: elements })
    }
  }

  const removeSubElement = (mainElement: string, subElement: string) => {
    const { ...array } = props.structure.hierarchy[mainElement]

    props.setChart((chart: any) => {
      const values = _.cloneDeep(chart.values)
      Object.keys(chart.values).forEach(society => {
        delete values[society][mainElement][subElement]
      })

      const structure = {
        ...props.structure,
        hierarchy: {
          ...props.structure.hierarchy,
          [mainElement]: Object.values(array),
        },
      }
      structure.hierarchy[mainElement] = structure.hierarchy[
        mainElement
      ].filter((el: string) => el != subElement)

      return {
        ...chart,
        values,
        structure,
      }
    })
  }

  const updateSubElement =
    (mainElement: string, subElement: string) => (e: React.ChangeEvent) => {
      const newSubElementName = (e.target as HTMLInputElement).value

      const array = [...props.structure.hierarchy[mainElement]]
      const index = array.indexOf(subElement)
      array[index] = newSubElementName

      props.setChart((chart: any) => {
        const values = _.cloneDeep(chart.values)

        Object.keys(chart.values).forEach(society => {
          values[society][newSubElementName] = values[society][subElement]
          delete values[society][subElement]
        })
        return {
          ...chart,
          values: values,
          structure: {
            ...props.structure,
            hierarchy: {
              ...props.structure.hierarchy,
              [mainElement]: Object.values(array),
            },
          },
        }
      })
    }

  const reorderSubElements = (
    mainElement: string,
    subElement: string,
    move: 'up' | 'down'
  ) => {
    const elements = [...props.structure.hierarchy[mainElement]]
    if (disabledMove(elements, subElement, move)) return
    switchElements(elements, subElement, move)

    setStructure({
      ...props.structure,
      hierarchy: { ...props.structure.hierarchy, [mainElement]: elements },
    })
  }

  const { confirm } = Modal

  const showConfirmRemoveElement = (
    mainElement: string,
    subElement?: string | undefined
  ) => {
    confirm({
      title: `Suppression ${subElement || mainElement}`,
      icon: <ExclamationCircleOutlined />,
      content: t(
        'ficheComparative.comparativeChart.popups.removeElementContent'
      ),
      okText: t('ficheComparative.comparativeChart.popups.ok'),
      okType: 'danger',
      cancelText: t('ficheComparative.comparativeChart.popups.cancel'),
      onOk() {
        subElement === undefined
          ? removeElement(mainElement)
          : removeSubElement(mainElement, subElement)
      },
    })
  }

  return (
    <BlockElement>
      {props.structure.elements.map((elementName: any, index: number) => {
        return (
          <Row gutter={[5, 20]} key={`${index}.${elementName}`}>
            <Col span={10}>
              <Input
                key={`input.element.${elementName}`}
                defaultValue={elementName}
                onBlur={updateElement(elementName)}
              />
            </Col>
            <Col span={2}>
              <Row gutter={[6, 0]}>
                <Col>
                  <Row>
                    <CaretUpFilled
                      style={arrowIconStyle(
                        disabledMove(
                          props.structure.elements,
                          elementName,
                          'up'
                        )
                      )}
                      onMouseDown={() => reorderElements(elementName, 'up')}
                    />
                  </Row>
                  <Row>
                    <CaretDownFilled
                      style={arrowIconStyle(
                        disabledMove(
                          props.structure.elements,
                          elementName,
                          'down'
                        )
                      )}
                      onMouseDown={() => reorderElements(elementName, 'down')}
                    />
                  </Row>
                </Col>
                <Col>
                  <MinusCircleTwoTone
                    key={`delete.element.${elementName}`}
                    style={minusIconStyle}
                    onMouseDown={() => showConfirmRemoveElement(elementName)}
                  />
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              {props.structure.hierarchy[elementName]?.map(
                (subElementName: string, index: number) => {
                  return (
                    <Row gutter={[5, 5]} key={index}>
                      <Col span={22}>
                        <Input
                          key={`input.subElement.${index}.${subElementName}`}
                          defaultValue={subElementName}
                          onBlur={updateSubElement(elementName, subElementName)}
                        />
                      </Col>
                      <Col span={2}>
                        <Row gutter={[6, 0]}>
                          <Col>
                            <Row>
                              <CaretUpFilled
                                style={arrowIconStyle(
                                  disabledMove(
                                    props.structure.hierarchy[elementName],
                                    subElementName,
                                    'up'
                                  )
                                )}
                                onMouseDown={() =>
                                  reorderSubElements(
                                    elementName,
                                    subElementName,
                                    'up'
                                  )
                                }
                              />
                            </Row>
                            <Row>
                              <CaretDownFilled
                                style={arrowIconStyle(
                                  disabledMove(
                                    props.structure.hierarchy[elementName],
                                    subElementName,
                                    'down'
                                  )
                                )}
                                onMouseDown={() =>
                                  reorderSubElements(
                                    elementName,
                                    subElementName,
                                    'down'
                                  )
                                }
                              />
                            </Row>
                          </Col>
                          <Col>
                            <MinusCircleTwoTone
                              key={`delete.subElement.${index}.${subElementName}`}
                              style={minusIconStyle}
                              onMouseDown={() =>
                                showConfirmRemoveElement(
                                  elementName,
                                  subElementName
                                )
                              }
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )
                }
              )}
              <Row gutter={[5, 5]} key={'Add'}>
                <Col>
                  <PlusCircleTwoTone
                    key={`AddTo.${elementName}`}
                    style={plusIconStyle}
                    onMouseDown={() => addSubElement(elementName, '')}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )
      })}
      <Row>
        <Col>
          <PlusCircleTwoTone
            key={'Add'}
            style={plusIconStyle}
            onMouseDown={() => addElement('')}
          />
        </Col>
      </Row>
    </BlockElement>
  )
}

export default ChartComposition
